/**
 * CreateReportTypeModal — migrated to unified modal system.
 * 5 fields: code, name, nameEn, description, unit.
 */

import { useState, useMemo, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ClipboardList, HashIcon, StickyNoteIcon, LanguagesIcon, Ruler } from 'lucide-react';
import toast from 'react-hot-toast';
import { FormModal, FormField, useVinylSubmit, MODAL_COLORS } from '@/components/modal';
import { useCreateReportType } from '@/features/reports/hooks';
import type { ReportTypeFormData } from '@/features/reports/types';

const UNIT_OPTIONS = ['per_unit', 'per_hour', 'per_day', 'per_week'] as const;

interface CreateReportTypeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export function CreateReportTypeModal({
  isOpen,
  onClose,
  onSuccess,
}: CreateReportTypeModalProps) {
  const { t, i18n } = useTranslation();
  const isHebrew = i18n.language === 'he';
  const createReportType = useCreateReportType();
  const modalContentRef = useRef<HTMLDivElement>(null);
  const { isAnimating, submitWithAnimation } = useVinylSubmit({
    modalContentRef,
    vinylLabel: 'REPORT TYPE',
  });

  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [nameEn, setNameEn] = useState('');
  const [description, setDescription] = useState('');
  const [unit, setUnit] = useState<string>('per_unit');
  const [quantityAlias, setQuantityAlias] = useState('');
  const [quantityAliasEn, setQuantityAliasEn] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setCode('');
      setName('');
      setNameEn('');
      setDescription('');
      setUnit('per_unit');
      setQuantityAlias('');
      setQuantityAliasEn('');
      setError('');
    }
  }, [isOpen]);

  const focusField = (id: string) => {
    requestAnimationFrame(() => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.focus({ preventScroll: true });
      }
    });
  };

  const validateForm = (): boolean => {
    setError('');
    if (!code.trim()) {
      setError(isHebrew ? '\u05E0\u05D0 \u05DC\u05D4\u05D6\u05D9\u05DF \u05E7\u05D5\u05D3' : 'Code is required');
      focusField('crt-code');
      return false;
    }
    if (!name.trim()) {
      setError(isHebrew ? '\u05E0\u05D0 \u05DC\u05D4\u05D6\u05D9\u05DF \u05E9\u05DD' : 'Name is required');
      focusField('crt-name');
      return false;
    }
    return true;
  };

  const isFormValid = useMemo(() => {
    return !!(code.trim() && name.trim());
  }, [code, name]);

  const handleSubmit = () => {
    const payload: ReportTypeFormData = {
      code: code.trim().toUpperCase(),
      name: name.trim(),
      nameEn: nameEn.trim() || undefined,
      description: description.trim() || undefined,
      quantityAlias: quantityAlias.trim() || undefined,
      quantityAliasEn: quantityAliasEn.trim() || undefined,
      unit,
    };

    submitWithAnimation({
      validate: validateForm,
      mutate: () => createReportType.mutateAsync(payload),
      onSuccess: () => {
        toast.success(t('reportTypesAdmin.createSuccess'));
        onSuccess();
        onClose();
      },
      onError: (err: any) => {
        const msg = err.response?.data?.message || '';
        if (msg.includes('already exists')) {
          setError(t('reportTypesAdmin.codeExists'));
        } else {
          setError(err.response?.data?.error || msg || t('reportTypesAdmin.createFailed'));
        }
      },
    });
  };

  const inputStyle = {
    backgroundColor: MODAL_COLORS.interactive,
    border: `1px solid ${MODAL_COLORS.border}`,
    color: MODAL_COLORS.text,
  };

  if (!isOpen) return null;

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title={t('reportTypesAdmin.createTitle')}
      isPending={createReportType.isPending}
      isAnimating={isAnimating}
      onSubmit={handleSubmit}
      onValidate={validateForm}
      isFormValid={isFormValid}
      modalContentRef={modalContentRef}
      vinylLabel="REPORT TYPE"
      error={error}
    >
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-2">
          <FormField icon={<HashIcon className="h-4 w-4" />} label={t('reportTypesAdmin.code')} required>
            <input
              id="crt-code"
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder=""
              className="w-full p-2.5 rounded-lg font-semibold text-base focus:outline-none focus:ring-2"
              style={inputStyle}
            />
          </FormField>
          <FormField icon={<ClipboardList className="h-4 w-4" />} label={t('reportTypesAdmin.name')} required>
            <input
              id="crt-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder=""
              className="w-full p-2.5 rounded-lg font-semibold text-base focus:outline-none focus:ring-2"
              style={inputStyle}
            />
          </FormField>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <FormField icon={<LanguagesIcon className="h-4 w-4" />} label={t('reportTypesAdmin.nameEn')}>
            <input
              id="crt-nameEn"
              type="text"
              value={nameEn}
              onChange={(e) => setNameEn(e.target.value)}
              placeholder=""
              className="w-full p-2.5 rounded-lg font-semibold text-base focus:outline-none focus:ring-2"
              style={inputStyle}
            />
          </FormField>
          <FormField icon={<Ruler className="h-4 w-4" />} label={t('reportTypesAdmin.unit')}>
            <select
              id="crt-unit"
              value={unit}
              onChange={(e) => setUnit(e.target.value)}
              className="w-full p-2.5 rounded-lg font-semibold text-base focus:outline-none focus:ring-2 cursor-pointer"
              style={inputStyle}
            >
              {UNIT_OPTIONS.map((u) => (
                <option key={u} value={u}>{t(`reportTypesAdmin.unit_${u}`)}</option>
              ))}
            </select>
          </FormField>
        </div>
        <FormField icon={<StickyNoteIcon className="h-4 w-4" />} label={t('reportTypesAdmin.description')}>
          <textarea
            id="crt-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="w-full p-2.5 rounded-lg font-semibold text-base focus:outline-none focus:ring-2"
            style={{ ...inputStyle, minHeight: '80px', resize: 'vertical' as const }}
          />
        </FormField>
        <div className="grid grid-cols-2 gap-2">
          <FormField icon={<ClipboardList className="h-4 w-4" />} label={t('reportTypesAdmin.quantityAlias')}>
            <input
              id="crt-quantityAlias"
              type="text"
              value={quantityAlias}
              onChange={(e) => setQuantityAlias(e.target.value)}
              className="w-full p-2.5 rounded-lg font-semibold text-base focus:outline-none focus:ring-2"
              style={inputStyle}
            />
          </FormField>
          <FormField icon={<ClipboardList className="h-4 w-4" />} label={t('reportTypesAdmin.quantityAliasEn')}>
            <input
              id="crt-quantityAliasEn"
              type="text"
              value={quantityAliasEn}
              onChange={(e) => setQuantityAliasEn(e.target.value)}
              className="w-full p-2.5 rounded-lg font-semibold text-base focus:outline-none focus:ring-2"
              style={inputStyle}
            />
          </FormField>
        </div>
      </div>
    </FormModal>
  );
}
